import { useCallback } from "react";

export const usePhotoUpload = (parkId) => {
  const uploadPhoto = useCallback(
    async (processedImage, onProgress) => {
      // Get park ID with multiple fallbacks
      let finalParkId = parkId;

      if (
        !finalParkId ||
        finalParkId === "undefined" ||
        finalParkId === "null"
      ) {
        const container = document.querySelector("#photo-upload-container");
        if (container) {
          finalParkId = container.dataset.parkId;
        }
      }

      const parkIdString = String(finalParkId).trim();

      if (
        !parkIdString ||
        parkIdString === "undefined" ||
        !/^\d+$/.test(parkIdString)
      ) {
        throw new Error(
          `Invalid park ID: "${parkIdString}". Please refresh the page.`
        );
      }

      // Handle file
      let fileToUpload = processedImage.compressedFile;
      if (fileToUpload.name === "blob" || !fileToUpload.name.includes(".")) {
        const ext =
          fileToUpload.type === "image/png"
            ? "png"
            : fileToUpload.type === "image/webp"
            ? "webp"
            : "jpg";
        const newName = `mobile_photo_${Date.now()}.${ext}`;

        fileToUpload = new File([fileToUpload], newName, {
          type: fileToUpload.type || "image/jpeg",
          lastModified: Date.now(),
        });
      }

      // Create FormData with explicit encoding to ensure data gets through
      const formData = new FormData();

      // Add the photo first
      formData.append("photo", fileToUpload);

      // CRITICAL: Add park_id in a way that mobile browsers won't mess with
      // Method 1: Standard way
      formData.append("park_id", parkIdString);

      // Method 2: Explicitly set (overwrites if needed)
      formData.set("park_id", parkIdString);

      // Method 3: Add as backup fields
      formData.append("parkId", parkIdString);
      formData.append("post_id", parkIdString);
      formData.append("id", parkIdString);

      // Add other required fields
      formData.append(
        "original_size",
        String(processedImage.originalSize || 0)
      );
      formData.append(
        "compression_ratio",
        String(processedImage.compressionRatio || "0")
      );

      // IMPORTANT: Log FormData contents to verify
      const formDataEntries = [];
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          formDataEntries.push(`${key}: File(${value.name}, ${value.size}b)`);
        } else {
          formDataEntries.push(`${key}: "${value}"`);
        }
      }

      // Verify park_id is present
      const parkIdPresent = formData.has("park_id");
      const parkIdValue = formData.get("park_id");

      if (!parkIdPresent || !parkIdValue) {
        console.error("CRITICAL: park_id missing from FormData!");
        throw new Error("Failed to add park ID to form data");
      }

      try {
        onProgress({ stage: "uploading", progress: 0 });

        const nonce = window.wpApiSettings?.nonce;
        const apiRoot = window.wpApiSettings?.root;

        if (!nonce || !apiRoot) {
          throw new Error(
            "WordPress API settings not found. Please refresh the page."
          );
        }

        const apiUrl = apiRoot + "parkhoppers/v1/upload-photo";

        // Headers include both the nonce and backup park_id
        const headers = {
          "X-WP-Nonce": nonce,
          "X-Debug-Park-ID": parkIdString, // This works as we saw in the logs
          "X-Debug-Source": "final-js",
        };

        const response = await fetch(apiUrl, {
          method: "POST",
          body: formData,
          headers: headers,
          credentials: "same-origin",
        });

        const responseText = await response.text();

        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error("JSON parse error:", parseError);
          throw new Error(
            `Server returned invalid response: ${responseText.substring(
              0,
              100
            )}...`
          );
        }

        if (!response.ok) {
          console.error("HTTP error:", response.status, result);

          // More specific error messages
          let errorMessage =
            result.message || `Upload failed: ${response.statusText}`;

          if (
            response.status === 400 &&
            result.message &&
            result.message.includes("park_id")
          ) {
            errorMessage = `Park ID issue detected. This appears to be a mobile browser form data bug. The park ID (${parkIdString}) was sent but not received properly by the server.`;
          }

          throw new Error(errorMessage);
        }

        if (!result.success) {
          console.error("Upload failed:", result);
          throw new Error(result.message || "Upload failed");
        }

        onProgress({ stage: "uploaded", progress: 1, result: result.data });

        return result.data;
      } catch (error) {
        console.error("Upload error:", error);
        onProgress({ stage: "error", error: error.message });
        throw error;
      }
    },
    [parkId]
  );

  return { uploadPhoto };
};
